import React, { useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { dayKeyOf, getEventOccurrenceKeys, partitionDatedUndated } from '../../utils/eventDates';
import './EventCalendar.scss';

const startOfDay = (d) => {
  const key = dayKeyOf(d);
  return key === null ? null : new Date(key);
};

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const key = dayKeyOf(value);
  return key === null ? null : new Date(key);
};

// Expand one event into its active occurrence dates (service dates preferred).
// Undated events (no usable date anywhere) yield [] and never land on a cell.
const getOccurrences = (event) => {
  if (!event) return [];
  return getEventOccurrenceKeys(event).map((key) => ({ date: new Date(key), event }));
};

const MONTH_LABEL = (cursor, locale = 'en-IN') =>
  cursor.toLocaleDateString(locale === 'kn' ? 'kn-IN' : 'en-IN', { month: 'long', year: 'numeric' });

const normalizeOrderType = (value) => {
  const n = String(value || 'own').toLowerCase().replace(/[\s-]/g, '');
  return n === 'suborder' || n === 'sub' ? 'suborder' : 'own';
};

const EventCalendar = forwardRef(({ events = [], selectedKey = null, onDaySelect }, ref) => {
  const { t, language } = useLanguage();
  const today = useMemo(() => startOfDay(new Date()), []);
  // Default view: current month with today pre-selected.
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [internalKey, setInternalKey] = useState(() => today.getTime());
  // Fully controlled by parent when `selectedKey` is provided (may be null = no selection).
  // Falls back to internal state only when parent doesn't control it.
  const isControlled = selectedKey !== undefined;
  const activeKey = isControlled ? selectedKey : internalKey;

  const occurrences = useMemo(() => {
    const list = [];
    (Array.isArray(events) ? events : []).forEach((ev) => {
      getOccurrences(ev).forEach((o) => list.push(o));
    });
    return list;
  }, [events]);

  const byDay = useMemo(() => {
    const map = new Map();
    occurrences.forEach((o) => {
      const k = o.date.getTime();
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(o.event);
    });
    return map;
  }, [occurrences]);

  const monthCells = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = first.getDay(); // Sunday start
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i += 1) {
      const d = new Date(year, month, 1 - (startOffset - i));
      cells.push({ date: d, outside: true });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push({ date: new Date(year, month, day), outside: false });
    }
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      const next = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1);
      cells.push({ date: next, outside: true });
    }
    return cells;
  }, [cursor]);

  const monthEventCount = useMemo(() => {
    let count = 0;
    byDay.forEach((list, key) => {
      const d = new Date(key);
      if (d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth()) {
        count += list.length;
      }
    });
    return count;
  }, [byDay, cursor]);

  // Events with no usable date can never appear on a grid cell. Surface the
  // count so edits that add/remove dates are visibly reflected instantly
  // instead of looking like a stale/cached day.
  const undatedCount = useMemo(
    () => partitionDatedUndated(Array.isArray(events) ? events : []).undated.length,
    [events],
  );

  const goMonth = (delta) => {
    setCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };
  const goToday = () => {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    setCursor(first);
    if (!isControlled) setInternalKey(today.getTime());
    if (onDaySelect) onDaySelect(new Date(today));
  };

  // Navigation is rendered by the parent in the section header — expose it via ref.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({ goMonth, goToday }), [today, isControlled, onDaySelect]);

  const weekdayLabels = useMemo(() => {
    // Sunday-first short labels, locale aware
    const base = new Date(2026, 5, 7); // a Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
      return d.toLocaleDateString(language === 'kn' ? 'kn-IN' : 'en-IN', { weekday: 'short' });
    });
  }, [language]);

  const localeTag = language === 'kn' ? 'kn-IN' : 'en-IN';

  const formatTimeShort = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    if (!text) return '';
    // ISO datetime → locale time
    if (/T\d{2}:\d{2}/.test(text)) {
      const d = parseDate(text);
      if (d) return d.toLocaleTimeString(localeTag, { hour: 'numeric', minute: '2-digit' });
    }
    // 24h "14:30" / "14:30:00" → locale time
    const hm = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (hm) {
      const h = Number(hm[1]);
      const m = Number(hm[2]);
      if (Number.isFinite(h) && Number.isFinite(m) && h < 24 && m < 60) {
        const d = new Date(2000, 0, 1, h, m);
        return d.toLocaleTimeString(localeTag, { hour: 'numeric', minute: '2-digit' });
      }
    }
    return text.length > 18 ? `${text.slice(0, 18)}…` : text;
  };

  // Services of a given event that fall on a specific day-key.
  const getServicesForDay = (ev, dayKey) => {
    if (!ev || !Array.isArray(ev.services)) return [];
    return ev.services.filter((s) => dayKeyOf(s && s.date) === dayKey);
  };

  const getPreviewDetails = (ev, dayKey) => {
    const onDay = getServicesForDay(ev, dayKey);
    const source = onDay.length ? onDay : [];
    const names = source.map((s) => s && s.name).filter(Boolean);
    const times = source.map((s) => formatTimeShort(s && s.time)).filter(Boolean);
    const location =
      (source.find((s) => s && s.location) || {}).location ||
      ev?.venue || ev?.location || ev?.address || ev?.city || '';
    return {
      names: names.slice(0, 2),
      extraNames: Math.max(0, names.length - 2),
      time: times[0] || formatTimeShort(ev?.time) || '',
      location: String(location || '').trim(),
    };
  };

  // Clicking a day selects/filters it — hide its hover card immediately so it
  // doesn't linger under the cursor. Cleared on pointer-leave / Escape.
  const [dismissedKey, setDismissedKey] = useState(null);

  const formatPreviewHeading = (date) => {
    try {
      const weekday = date.toLocaleDateString(localeTag, { weekday: 'short' });
      const rest = date.toLocaleDateString(localeTag, { day: 'numeric', month: 'short' });
      return { weekday, rest };
    } catch {
      return { weekday: '', rest: date.toDateString() };
    }
  };

  return (
    <div className="evcal" role="region" aria-label={t('dashboard.calendarView')}>
      <div className="evcal__toolbar">
        <h3 className="evcal__month">{MONTH_LABEL(cursor, language)}</h3>
        <span className="evcal__count" aria-live="polite">
          {monthEventCount === 0
            ? t('dashboard.noEventsThisMonth')
            : t('dashboard.eventsThisMonth').replace('{count}', String(monthEventCount))}
          {undatedCount > 0 && (
            <>
              {' · '}
              {t('dashboard.undatedNote').replace('{count}', String(undatedCount))}
            </>
          )}
        </span>
      </div>

      <div className="evcal__weekdays" aria-hidden="true">
        {weekdayLabels.map((w) => (
          <span key={w} className="evcal__weekday">{w}</span>
        ))}
      </div>

      <div className="evcal__grid" role="grid" aria-label={MONTH_LABEL(cursor, language)}>
        {monthCells.map(({ date, outside }, index) => {
          const dayStart = startOfDay(date);
          const key = dayStart ? dayStart.getTime() : new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
          const dayEvents = byDay.get(key) || [];
          const isToday = key === today.getTime();
          const isSelected = activeKey !== null && key === activeKey;
          const visible = dayEvents.slice(0, 2);
          const extra = dayEvents.length - visible.length;
          const col = index % 7;
          const row = Math.floor(index / 7);
          const totalRows = Math.ceil(monthCells.length / 7);
          const align = col <= 1 ? 'left' : col >= 5 ? 'right' : 'center';
          const placeAbove = row >= totalRows - 2;
          const heading = formatPreviewHeading(date);
          const previewList = dayEvents.slice(0, 3);
          const previewExtra = dayEvents.length - previewList.length;
          return (
            <div
              key={`${key}-${outside ? 'o' : 'm'}`}
              className={[
                'evcal__cell',
                `evcal__cell--${align}`,
                placeAbove ? 'evcal__cell--above' : 'evcal__cell--below',
                dismissedKey === key ? 'evcal__cell--dismissed' : '',
              ].join(' ').trim()}
              onMouseLeave={() => {
                setDismissedKey((prev) => (prev === key ? null : prev));
              }}
            >
            <button
              type="button"
              role="gridcell"
              aria-selected={isSelected}
              aria-label={`${date.toDateString()}${dayEvents.length ? `, ${dayEvents.length} events` : ''}`}
              onClick={(e) => {
                if (!isControlled) setInternalKey(key);
                if (onDaySelect) onDaySelect(new Date(date));
                // Close the hover card on click (it would otherwise stay
                // visible under the cursor after selection).
                setDismissedKey(key);
                if (e && e.currentTarget && e.currentTarget.blur) e.currentTarget.blur();
              }}
              onKeyDown={(e) => {
                if (e && e.key === 'Escape') {
                  setDismissedKey(key);
                  if (e.currentTarget && e.currentTarget.blur) e.currentTarget.blur();
                }
              }}
              onMouseEnter={() => {
                // Re-arm hover for a different day after a previous dismissal.
                setDismissedKey((prev) => (prev !== null && prev !== key ? null : prev));
              }}
              onFocus={() => {
                setDismissedKey((prev) => (prev !== null && prev !== key ? null : prev));
              }}
              className={[
                'evcal__day',
                outside ? 'evcal__day--outside' : '',
                isToday ? 'evcal__day--today' : '',
                isSelected ? 'evcal__day--selected' : '',
                dayEvents.length ? 'evcal__day--has-events' : '',
              ].join(' ').trim()}
            >
              <span className="evcal__date-num">{date.getDate()}</span>
              {dayEvents.length > 0 && (
                <span className="evcal__bars" aria-hidden="true">
                  {visible.map((ev, i) => {
                    return (
                      <span
                        key={`${ev?.id || ev?._id || i}`}
                        className={`evcal__bar evcal__bar--${normalizeOrderType(ev?.orderType)}`}
                      />
                    );
                  })}
                  {extra > 0 && <span className="evcal__more">+{extra}</span>}
                </span>
              )}
            </button>
            {/* Hover preview card — only for days that have events */}
            {dayEvents.length > 0 && (
            <div
              className="evcal__preview evcal__preview--events"
              aria-hidden="true"
            >
              <div className="evcal__preview-head">
                <span className="evcal__preview-date">
                  <span className="evcal__preview-dow">{heading.weekday}</span>
                  <span className="evcal__preview-dm">{heading.rest}</span>
                </span>
                <span className="evcal__preview-count">
                  {dayEvents.length === 1
                    ? `1 ${t('dashboard.eventSingular')}`
                    : t('dashboard.eventsOnDay').replace('{count}', String(dayEvents.length))}
                </span>
              </div>
              <ul className="evcal__preview-list">
                  {previewList.map((ev, i) => {
                    const client = ev?.clientName || ev?.title || ev?.name || t('dashboard.untitledEvent');
                    const details = getPreviewDetails(ev, key);
                    const orderType = normalizeOrderType(ev?.orderType);
                    return (
                      <li key={`${ev?.id || ev?._id || client}-${i}`} className="evcal__preview-item">
                        <span
                          className={`evcal__preview-dot evcal__preview-dot--${orderType}`}
                        />
                        <span className="evcal__preview-item-body">
                          <span className="evcal__preview-name">{client}</span>
                          {(details.names.length > 0 || details.time || details.location) && (
                            <span className="evcal__preview-meta">
                              {details.names.length > 0 && (
                                <span className="evcal__preview-services">
                                  {details.names.join(' · ')}
                                  {details.extraNames > 0 && ` +${details.extraNames}`}
                                </span>
                              )}
                              {(details.time || details.location) && (
                                <span className="evcal__preview-sub">
                                  {[details.time, details.location].filter(Boolean).join(' · ')}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                        <span className={`evcal__preview-tag evcal__preview-tag--${orderType}`}>
                          {orderType === 'suborder' ? t('dashboard.orderSub') : t('dashboard.orderOwn')}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              <div className="evcal__preview-foot">
                {previewExtra > 0 ? (
                  <span className="evcal__preview-more">
                    {t('dashboard.calendarPreviewMore').replace('{count}', String(previewExtra))}
                  </span>
                ) : (
                  <span className="evcal__preview-hint">{t('dashboard.calendarPreviewHint')}</span>
                )}
              </div>
              <span className="evcal__preview-arrow" aria-hidden="true" />
            </div>
            )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default EventCalendar;
